import { useState, useEffect } from 'react'
import { useToast } from '../../context/ToastContext'
import { getApiBaseUrl } from '../../utils/api'
import { trackBeginCheckout } from '../../utils/analytics'

export default function StripeCheckoutModal({ onClose, package: pkg }) {
  const [isProcessing, setIsProcessing] = useState(false)
  const { showToast } = useToast()

  // Track begin_checkout when modal opens
  useEffect(() => {
    if (pkg) {
      trackBeginCheckout(pkg.price, pkg.id)
    }
  }, [pkg])

  const handleCheckout = async () => {
    if (!pkg) return

    try {
      setIsProcessing(true)

      const apiUrl = getApiBaseUrl()

      // Call API to create Stripe Checkout session
      const response = await fetch(`${apiUrl}/v1/payment/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({
          credit_package_id: pkg.id,
          success_url: `${window.location.origin}/payment/success`,
          cancel_url: `${window.location.origin}/payment/cancel`,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to create checkout session')
      }

      const data = await response.json()

      // Redirect to Stripe Checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url
      } else {
        showToast('error', 'Checkout URL not provided', 'Error')
      }
    } catch (error) {
      console.error('Checkout error:', error)
      showToast('error', error.message, 'Checkout Failed')
    } finally {
      setIsProcessing(false)
    }
  }

  if (!pkg) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-8 max-w-md w-full mx-4 animate-fade-in">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Confirm Purchase</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-3xl font-light leading-none"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Package Summary */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-gray-700 dark:text-gray-300 font-medium">Package</span>
            <span className="text-xl font-bold text-gray-900 dark:text-white">{pkg.name}</span>
          </div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-gray-700 dark:text-gray-300 font-medium">Price</span>
            <span className="text-2xl font-bold text-primary-600">${pkg.price}</span>
          </div>
          <div className="border-t border-gray-300 dark:border-gray-600 pt-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-700 dark:text-gray-300 font-medium">You receive</span>
              <span className="text-lg font-bold text-green-600">
                {pkg.credits} credits
                {pkg.bonus > 0 && <span className="text-sm ml-2 text-green-700">(+{pkg.bonus} bonus)</span>}
              </span>
            </div>
          </div>
        </div>

        {/* Terms */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
          <div className="text-sm text-blue-900 dark:text-blue-200">
            <strong>Note:</strong> You will be redirected to Stripe Checkout for payment processing. Your billing information will be securely stored.
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCheckout}
            disabled={isProcessing}
            className="flex-1 px-4 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white rounded-lg font-semibold transition-colors"
          >
            {isProcessing ? 'Processing...' : 'Proceed to Payment'}
          </button>
        </div>
      </div>
    </div>
  )
}
